import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { supabase } from '../lib/supabaseClient'; // Import the supabase client
import { isPublicRoute } from '../components/utils';
import { clearClientAuthState, clearClientIdentityState } from '../utils/authCleanup';
import posthog from 'posthog-js';

const createEmptyProfileCache = () => ({
    user: null,
    timestamp: 0,
    userId: null,
    accessToken: null,
    pending: false,
    pendingPromise: null,
    pendingAccessToken: null
});

// Cooldown applied when the backend answers 429 and sends no Retry-After header.
const PROFILE_RATE_LIMIT_DEFAULT_COOLDOWN_MS = 30 * 1000;

// 1. Create the Context
export const AuthContext = createContext({
    session: null,
    user: null,
    isGuest: false,
    loading: true,
    profileRefreshing: false,
    login: async () => { throw new Error('Login function not implemented'); },
    loginWithOAuth: async () => { throw new Error('OAuth login function not implemented'); },
    ensureUserProfile: async () => { throw new Error('Ensure profile function not implemented'); },
    logout: async () => { throw new Error('Logout function not implemented'); },
    register: async () => { throw new Error('Register function not implemented'); },
});

// 2. Create the Provider Component
export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileRefreshing, setProfileRefreshing] = useState(false);

    // Add cache to prevent duplicate profile fetches
    const profileCache = useRef(createEmptyProfileCache());
    const profileRateLimitedUntilRef = useRef(0);
    const authResolutionId = useRef(0);
    const activeSessionUserId = useRef(null);
    const activeSessionAccessToken = useRef(null);
    const resolvedUser = useRef(null);
    const authStateChangeTimerIds = useRef(new Set());
    const CACHE_DURATION = 30000; // 30 seconds cache

    const getSessionUserId = (session) => session?.user?.id || null;
    const getSessionAccessToken = (session) => session?.access_token || null;

    const startAuthResolution = () => {
        authResolutionId.current += 1;
        return authResolutionId.current;
    };

    const isCurrentAuthResolution = (resolutionId) => (
        authResolutionId.current === resolutionId
    );

    const setActiveSession = (nextSession) => {
        activeSessionUserId.current = getSessionUserId(nextSession);
        activeSessionAccessToken.current = getSessionAccessToken(nextSession);
        setSession(nextSession);
    };

    const areProfilesEqual = (firstProfile, secondProfile) => (
        JSON.stringify(firstProfile) === JSON.stringify(secondProfile)
    );

    const setResolvedUser = (nextUser) => {
        resolvedUser.current = nextUser;
        setUser(prevUser => {
            const profileChanged = !areProfilesEqual(prevUser, nextUser);
            if (profileChanged) {
                if (nextUser && !prevUser) {
                    posthog.identify(nextUser._id || nextUser.id, {
                        email: nextUser.email,
                        username: nextUser.username,
                    });
                }
                return nextUser;
            }

            return prevUser;
        });
    };

    const isProfileRequestSessionCurrent = (activeSession) => (
        activeSessionUserId.current === getSessionUserId(activeSession) &&
        activeSessionAccessToken.current === getSessionAccessToken(activeSession)
    );

    const isSameActiveSession = (nextSession) => (
        activeSessionUserId.current === getSessionUserId(nextSession) &&
        activeSessionAccessToken.current === getSessionAccessToken(nextSession)
    );

    const isSameActiveUser = (nextSession) => (
        activeSessionUserId.current === getSessionUserId(nextSession)
    );

    const shouldResolveAuthEventQuietly = (event, nextSession) => (
        nextSession &&
        resolvedUser.current &&
        (
            (event === 'TOKEN_REFRESHED' && isSameActiveUser(nextSession)) ||
            ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && isSameActiveSession(nextSession))
        )
    );

    const shouldShowProfileRefreshing = (quiet, nextSession) => (
        quiet &&
        nextSession &&
        !getCachedProfile(getSessionUserId(nextSession), getSessionAccessToken(nextSession))
    );

    const isOAuthCallbackRoute = () => (
        typeof window !== 'undefined' &&
        window.location.pathname === '/auth/callback'
    );

    const shouldLetOAuthCallbackEnsureProfile = (nextSession) => (
        !!nextSession && isOAuthCallbackRoute()
    );

    const canApplyAuthResolutionForSession = (resolutionId, activeSession) => (
        isCurrentAuthResolution(resolutionId) ||
        isProfileRequestSessionCurrent(activeSession)
    );

    const resetResolvedIdentity = (source) => {
        profileCache.current = createEmptyProfileCache();
        setResolvedUser(null, source);
        setProfileRefreshing(false);
    };

    const redirectToLoginIfProtectedRoute = () => {
        if (typeof window === 'undefined') return;
        if (!isPublicRoute(window.location.pathname)) {
            window.location.href = '/login';
        }
    };

    const clearStoredAuthState = async () => {
        try { await supabase.auth.signOut(); } catch (_) {
            // State cleanup below still needs to run if Supabase sign-out fails.
        }
        clearClientAuthState();
        setActiveSession(null);
        resetResolvedIdentity('clearStoredAuthState');
    };

    const clearStoredProfile = () => {
        try { localStorage.removeItem('user'); } catch (_) {
            // Ignore storage access errors; React state still moves to null.
        }
    };

    const getPendingProfilePromise = (cacheKey, accessToken) => {
        if (
            profileCache.current.pending &&
            profileCache.current.pendingPromise &&
            profileCache.current.userId === cacheKey &&
            profileCache.current.pendingAccessToken === accessToken
        ) {
            return profileCache.current.pendingPromise;
        }
        return null;
    };

    const getCachedProfile = (cacheKey, accessToken) => {
        const now = Date.now();
        if (profileCache.current.user &&
            profileCache.current.timestamp > now - CACHE_DURATION &&
            profileCache.current.userId === cacheKey &&
            profileCache.current.accessToken === accessToken) {
            return profileCache.current.user;
        }
        return null;
    };

    const startPendingProfileRequest = (cacheKey, pendingPromise, accessToken) => {
        const currentCache = profileCache.current;
        const isSameSession = (
            currentCache.userId === cacheKey &&
            currentCache.accessToken === accessToken
        );

        profileCache.current = {
            user: isSameSession ? currentCache.user : null,
            timestamp: isSameSession ? currentCache.timestamp : 0,
            userId: cacheKey,
            accessToken: isSameSession ? currentCache.accessToken : null,
            pending: true,
            pendingPromise,
            pendingAccessToken: accessToken
        };
    };

    const clearPendingProfileRequest = (pendingPromise) => {
        if (profileCache.current.pendingPromise === pendingPromise) {
            profileCache.current = {
                ...profileCache.current,
                pending: false,
                pendingPromise: null,
                pendingAccessToken: null
            };
        }
    };

    const clearCachedProfileForUser = (cacheKey) => {
        if (profileCache.current.userId !== cacheKey) {
            return false;
        }

        profileCache.current = {
            ...profileCache.current,
            user: null,
            timestamp: 0,
            accessToken: null
        };
        return true;
    };

    const clearCachedProfileForCurrentSession = (activeSession, activeCacheKey) => {
        if (isProfileRequestSessionCurrent(activeSession) && clearCachedProfileForUser(activeCacheKey)) {
            clearStoredProfile();
        }
    };

    // Last-known profile for a user, ignoring the cache TTL. Used only for
    // transient failures (rate limiting) where dropping the resolved identity
    // would log the user out of the UI over a temporary outage.
    const getStaleProfileForUser = (cacheKey) => (
        profileCache.current.userId === cacheKey ? profileCache.current.user : null
    );

    const scheduleAuthStateResolution = (resolveAuthStateChange) => {
        const timerId = setTimeout(() => {
            authStateChangeTimerIds.current.delete(timerId);
            resolveAuthStateChange();
        }, 0);
        authStateChangeTimerIds.current.add(timerId);
    };

    const clearScheduledAuthStateResolutions = () => {
        authStateChangeTimerIds.current.forEach((timerId) => clearTimeout(timerId));
        authStateChangeTimerIds.current.clear();
    };

    // Function to fetch user profile data from your backend
    const fetchUserProfile = async (session, options = {}) => {
        const { allowSessionRefresh = true } = options;

        if (!session || !session.user || !session.user.id) {
            console.warn('[AuthProvider_DEBUG] fetchUserProfile: called without a valid session or user ID.', { session });
            return null;
        }
        if (!session.access_token) {
            console.warn('[AuthProvider_DEBUG] fetchUserProfile: No access_token in session.', { session });
            return null;
        }

        // Check cache first
        const cacheKey = session.user.id;
        const pendingProfilePromise = getPendingProfilePromise(cacheKey, session.access_token);

        if (pendingProfilePromise) {
            return pendingProfilePromise;
        }

        const cachedProfile = getCachedProfile(cacheKey, session.access_token);
        if (cachedProfile) {
            return cachedProfile;
        }

        // Backend rate limiting is transient and shared across all requests:
        // issuing more profile fetches during the cooldown only deepens the
        // outage. Serve the last-known profile (or null) without touching the
        // network until the cooldown expires.
        if (Date.now() < profileRateLimitedUntilRef.current) {
            console.warn('[AuthProvider_DEBUG] fetchUserProfile: Skipping profile request during rate-limit cooldown.');
            return getStaleProfileForUser(cacheKey);
        }

        // Captured before startPendingProfileRequest resets the cached user for
        // a rotated access token; the rate-limit path falls back to this.
        const profileBeforeRequest = getStaleProfileForUser(cacheKey);

        const profileUrl = `${import.meta.env.VITE_API_URL}/auth/profile`;

        const requestUserProfile = async (activeSession, activeCacheKey) => {
            if (!activeSession?.access_token) {
                console.warn('[AuthProvider_DEBUG] fetchUserProfile: No access_token in active session.', { activeSession });
                return { user: null, unauthorized: false };
            }

            const response = await fetch(profileUrl, {
                headers: {
                    'Authorization': `Bearer ${activeSession.access_token}`
                }
            });

            if (!response.ok) {
                console.error(`[AuthProvider_DEBUG] fetchUserProfile: Failed to fetch profile. Status: ${response.status}`);
                try {
                    const errorBody = await response.text();
                    console.error('[AuthProvider_DEBUG] fetchUserProfile: Error response body:', errorBody);
                } catch (e) {
                    console.error('[AuthProvider_DEBUG] fetchUserProfile: Could not get error response body.');
                }

                if (response.status === 401 && !isProfileRequestSessionCurrent(activeSession)) {
                    console.warn('[AuthProvider_DEBUG] fetchUserProfile: Ignoring stale unauthorized response for inactive session.', { activeCacheKey });
                    return { user: null, unauthorized: false };
                }

                if (response.status === 429) {
                    const retryAfterSeconds = Number(response.headers?.get?.('Retry-After'));
                    const cooldownMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
                        ? retryAfterSeconds * 1000
                        : PROFILE_RATE_LIMIT_DEFAULT_COOLDOWN_MS;
                    profileRateLimitedUntilRef.current = Date.now() + cooldownMs;
                    console.warn(`[AuthProvider_DEBUG] fetchUserProfile: Rate limited. Pausing profile requests for ${Math.round(cooldownMs / 1000)}s and keeping the last-known profile.`);

                    // startPendingProfileRequest wipes the cached user when the
                    // access token rotates. Restore the pre-request profile so
                    // this resolution - and every one during the cooldown -
                    // keeps the signed-in identity instead of logging out.
                    const lastKnownProfile = getStaleProfileForUser(activeCacheKey) ?? profileBeforeRequest;
                    if (lastKnownProfile) {
                        profileCache.current = {
                            ...profileCache.current,
                            user: lastKnownProfile,
                            userId: activeCacheKey
                        };
                    }
                    return { user: lastKnownProfile, unauthorized: false };
                }

                if (response.status !== 401) {
                    clearCachedProfileForCurrentSession(activeSession, activeCacheKey);
                }
                return { user: null, unauthorized: response.status === 401 };
            }

            const profileData = await response.json();

            if (profileData && profileData.user) {
                if (!isProfileRequestSessionCurrent(activeSession)) {
                    console.warn('[AuthProvider_DEBUG] fetchUserProfile: Ignoring stale profile response for inactive session.', { activeCacheKey });
                    return { user: null, unauthorized: false };
                }

                localStorage.setItem('user', JSON.stringify(profileData.user));
                profileRateLimitedUntilRef.current = 0;

                // Update cache
                profileCache.current = {
                    user: profileData.user,
                    timestamp: Date.now(),
                    userId: activeCacheKey,
                    accessToken: activeSession.access_token,
                    pending: false,
                    pendingPromise: null,
                    pendingAccessToken: null
                };

                return { user: profileData.user, unauthorized: false };
            }

            console.warn('[AuthProvider_DEBUG] fetchUserProfile: Backend profile response did not contain user object.', { profileData });
            clearCachedProfileForCurrentSession(activeSession, activeCacheKey);
            return { user: null, unauthorized: false };
        };

        let pendingPromise;
        pendingPromise = (async () => {
            try {
                let profileResult;
                try {
                    profileResult = await requestUserProfile(session, cacheKey);
                } catch (error) {
                    console.error('[AuthProvider_DEBUG] fetchUserProfile: Error during fetch or JSON parsing:', error);
                    clearCachedProfileForCurrentSession(session, cacheKey);
                    return null;
                }

                if (!profileResult.unauthorized) {
                    return profileResult.user;
                }

                clearPendingProfileRequest(pendingPromise);
                if (!allowSessionRefresh) {
                    console.warn('[AuthProvider_DEBUG] fetchUserProfile: Received 401 while session refresh is disabled. Purging auth state.');
                    if (isProfileRequestSessionCurrent(session)) {
                        await clearStoredAuthState();
                        redirectToLoginIfProtectedRoute();
                    }
                    return null;
                }

                console.warn('[AuthProvider_DEBUG] fetchUserProfile: Received 401. Attempting single session refresh.');
                if (!isProfileRequestSessionCurrent(session)) {
                    console.warn('[AuthProvider_DEBUG] fetchUserProfile: Skipping refresh for inactive session user.', { cacheKey });
                    return null;
                }

                let refreshResult;
                try {
                    refreshResult = await supabase.auth.refreshSession();
                } catch (refreshErr) {
                    if (!isProfileRequestSessionCurrent(session)) {
                        console.warn('[AuthProvider_DEBUG] refreshSession threw for an inactive session. Keeping current auth state.', refreshErr);
                        return null;
                    }

                    console.warn('[AuthProvider_DEBUG] refreshSession threw. Purging auth state.', refreshErr);
                    await clearStoredAuthState();
                    redirectToLoginIfProtectedRoute();
                    return null;
                }

                const { data, error } = refreshResult;
                if (error || !data?.session) {
                    if (!isProfileRequestSessionCurrent(session)) {
                        console.warn('[AuthProvider_DEBUG] refreshSession failed for an inactive session. Keeping current auth state.');
                        return null;
                    }

                    console.warn('[AuthProvider_DEBUG] refreshSession failed or no session. Purging auth state.');
                    await clearStoredAuthState();
                    redirectToLoginIfProtectedRoute();
                    return null;
                }

                const refreshedSession = data.session;
                const refreshedCacheKey = refreshedSession.user?.id || cacheKey;
                if (!isProfileRequestSessionCurrent(session)) {
                    console.warn('[AuthProvider_DEBUG] fetchUserProfile: Ignoring refreshed session because auth state changed while refresh was in flight.', { cacheKey, refreshedCacheKey });
                    return null;
                }

                setActiveSession(refreshedSession);
                const cachedProfileAfterRefresh = getCachedProfile(refreshedCacheKey, refreshedSession.access_token);

                if (cachedProfileAfterRefresh) {
                    return cachedProfileAfterRefresh;
                }

                const pendingProfileAfterRefresh = getPendingProfilePromise(refreshedCacheKey, refreshedSession.access_token);
                if (pendingProfileAfterRefresh) {
                    return pendingProfileAfterRefresh;
                }

                startPendingProfileRequest(refreshedCacheKey, pendingPromise, refreshedSession.access_token);

                let retryResult;
                try {
                    retryResult = await requestUserProfile(refreshedSession, refreshedCacheKey);
                } catch (error) {
                    console.error('[AuthProvider_DEBUG] fetchUserProfile: Error during refreshed profile fetch or JSON parsing:', error);
                    clearCachedProfileForCurrentSession(refreshedSession, refreshedCacheKey);
                    return null;
                }

                if (retryResult.unauthorized) {
                    if (!isProfileRequestSessionCurrent(refreshedSession)) {
                        console.warn('[AuthProvider_DEBUG] refreshed session was rejected after auth state changed. Keeping current auth state.');
                        return null;
                    }

                    console.warn('[AuthProvider_DEBUG] refreshed session was rejected. Purging auth state.');
                    await clearStoredAuthState();
                    redirectToLoginIfProtectedRoute();
                    return null;
                }

                return retryResult.user;
            } catch (error) {
                console.error('[AuthProvider_DEBUG] fetchUserProfile: Error during fetch or JSON parsing:', error);
                clearCachedProfileForCurrentSession(session, cacheKey);
                return null;
            } finally {
                clearPendingProfileRequest(pendingPromise);
            }
        })();

        // Mark as pending to prevent concurrent requests. Duplicate callers await
        // the same promise instead of receiving a stale null user while auth is valid.
        startPendingProfileRequest(cacheKey, pendingPromise, session.access_token);

        return pendingPromise;
    };

    const ensureUserProfile = async () => {
        const resolutionId = startAuthResolution();
        setLoading(true);
        setProfileRefreshing(false);
        let latestSession = null;

        try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            latestSession = session;

            if (sessionError) {
                throw sessionError;
            }

            if (!latestSession?.access_token || !latestSession?.user?.id) {
                throw new Error('No active login session found.');
            }

            setActiveSession(latestSession);

            const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/ensure-profile`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${latestSession.access_token}`
                }
            });

            let payload = null;
            try {
                payload = await response.json();
            } catch (_) {
                payload = null;
            }

            if (!response.ok) {
                throw new Error(payload?.error || 'Nova could not prepare your profile. Please try again.');
            }

            if (!payload?.user) {
                throw new Error('Nova could not load your profile. Please try again.');
            }

            if (!canApplyAuthResolutionForSession(resolutionId, latestSession)) {
                return payload.user;
            }

            localStorage.setItem('user', JSON.stringify(payload.user));
            profileCache.current = {
                user: payload.user,
                timestamp: Date.now(),
                userId: latestSession.user.id,
                accessToken: latestSession.access_token,
                pending: false,
                pendingPromise: null,
                pendingAccessToken: null
            };
            setResolvedUser(payload.user, 'ensureUserProfile');
            return payload.user;
        } catch (error) {
            console.error('[AuthProvider_DEBUG] ensureUserProfile error:', error);
            if (canApplyAuthResolutionForSession(resolutionId, latestSession)) {
                setResolvedUser(null, 'ensureUserProfile');
            }
            throw error;
        } finally {
            if (canApplyAuthResolutionForSession(resolutionId, latestSession)) {
                setProfileRefreshing(false);
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        const initialResolutionId = startAuthResolution();
        setLoading(true);
        supabase.auth.getSession().then(async ({ data: { session: initialSupabaseSession } }) => { // Renamed to avoid conflict
            if (!isCurrentAuthResolution(initialResolutionId)) {
                console.warn('[AuthProvider_DEBUG] Initial effect: Ignoring stale initial session result.');
                return;
            }

            setActiveSession(initialSupabaseSession);
            if (shouldLetOAuthCallbackEnsureProfile(initialSupabaseSession)) {
                setProfileRefreshing(false);
                setLoading(false);
                return;
            }

            const profile = initialSupabaseSession ? await fetchUserProfile(initialSupabaseSession) : null;

            if (!isCurrentAuthResolution(initialResolutionId)) {
                console.warn('[AuthProvider_DEBUG] Initial effect: Ignoring stale profile result.');
                return;
            }

            setResolvedUser(profile, 'Initial effect');
            setProfileRefreshing(false);
            setLoading(false);
        }).catch(error => {
             console.error('[AuthProvider_DEBUG] Error getting initial session:', error);
             if (isCurrentAuthResolution(initialResolutionId)) {
                 setProfileRefreshing(false);
                 setLoading(false);
             }
        });

        const resolveAuthStateChange = async (event, currentSession, currentResolutionId) => {
            try {
                const profile = currentSession ? await fetchUserProfile(currentSession, {
                    allowSessionRefresh: event !== 'TOKEN_REFRESHED'
                }) : null;

                if (!isCurrentAuthResolution(currentResolutionId)) {
                    console.warn('[AuthProvider_DEBUG] onAuthStateChange: Ignoring stale auth event result.', { event });
                    return;
                }

                setResolvedUser(profile, 'onAuthStateChange');
                setProfileRefreshing(false);
                setLoading(false);
            } catch (error) {
                console.error('[AuthProvider_DEBUG] onAuthStateChange: Error resolving auth event.', error);
                if (isCurrentAuthResolution(currentResolutionId)) {
                    setResolvedUser(null, 'onAuthStateChange');
                    setProfileRefreshing(false);
                    setLoading(false);
                }
            }
        };

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, currentSession) => { // Renamed to avoid conflict
                const previousSessionUserId = activeSessionUserId.current;
                const nextSessionUserId = getSessionUserId(currentSession);
                const didIdentityChange = (
                    previousSessionUserId &&
                    nextSessionUserId &&
                    previousSessionUserId !== nextSessionUserId
                );

                if (didIdentityChange) {
                    clearClientIdentityState();
                    resetResolvedIdentity('onAuthStateChange identity change');
                }

                const quiet = shouldResolveAuthEventQuietly(event, currentSession);
                const currentResolutionId = startAuthResolution();
                if (!quiet) {
                    setProfileRefreshing(false);
                    setLoading(true);
                } else {
                    setProfileRefreshing(shouldShowProfileRefreshing(quiet, currentSession));
                }
                setActiveSession(currentSession);

                if (shouldLetOAuthCallbackEnsureProfile(currentSession)) {
                    setProfileRefreshing(false);
                    setLoading(false);
                    return;
                }

                // Supabase waits for async auth callbacks. Keep this callback synchronous
                // so profile recovery can safely call Supabase auth methods after the event.
                scheduleAuthStateResolution(() => {
                    resolveAuthStateChange(event, currentSession, currentResolutionId);
                });
            }
        );

        return () => {
            startAuthResolution();
            clearScheduledAuthStateResolutions();
            activeSessionUserId.current = null;
            activeSessionAccessToken.current = null;
            resolvedUser.current = null;
            subscription?.unsubscribe();
        };
    }, []); // Empty dependency array means this runs once on mount and cleans up on unmount

    // --- Auth Action Functions (using Supabase client) ---
    const login = async (credentials) => {
        setLoading(true);
        try {
            const { data, error } = await supabase.auth.signInWithPassword(credentials);
            if (error) {
                console.error('[AuthProvider_DEBUG] Supabase signInWithPassword error:', error);
                throw error;
            }
            // State will update via onAuthStateChange listener
            if (!data?.session) {
                setLoading(false);
            }
            return { data };
        } catch (error) {
            console.error('[AuthProvider_DEBUG] login function catch block error:', error);
            localStorage.removeItem('user');
            // localStorage.removeItem('token'); // Not strictly necessary if session token isn't stored separately here
            setLoading(false); // Ensure loading is false on error path
            throw error;
        }
    };

    const loginWithOAuth = async (provider, options = {}) => {
        setLoading(true);
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider,
                options,
            });

            if (error) {
                console.error('[AuthProvider_DEBUG] Supabase signInWithOAuth error:', error);
                throw error;
            }

            return { data };
        } catch (error) {
            console.error('[AuthProvider_DEBUG] loginWithOAuth catch block error:', error);
            setLoading(false);
            throw error;
        }
    };

    const logout = async () => {
        setLoading(true);
        try {
            const { error } = await supabase.auth.signOut();
            if (error) {
                 console.error('[AuthProvider_DEBUG] Supabase signOut error:', error);
                 throw error;
            }
            posthog.reset();
            clearClientAuthState();

            // Reset internal profile cache
            resetResolvedIdentity('logout');
            activeSessionUserId.current = null;
            activeSessionAccessToken.current = null;

            // Session & user state will be set to null by onAuthStateChange
        } catch (error) {
             console.error('[AuthProvider_DEBUG] logout function catch block error:', error);
             // Still ensure loading is false even if signOut fails for some reason
        } finally {
            setLoading(false);
        }
    };

    // TODO: Implement register function using supabase.auth.signUp if needed
    // and potentially calling your backend registration endpoint afterwards.
    const register = async (details) => {
        console.warn('[AuthProvider] Register function not implemented.');
        // Example:
        // setLoading(true);
        // try {
        //   const { data, error } = await supabase.auth.signUp({ email, password });
        //   if (error) throw error;
        //   // If successful, call backend /auth/register endpoint with details + Supabase ID
        //   // ... await axiosInstance.post('/auth/register', { ...details, supabaseId: data.user.id }); ...
        // } catch (error) { ... } finally { setLoading(false); }
        throw new Error('Register not implemented');
    };
    // ------------------------------------------------------

    const value = {
        session,
        user,
        isGuest: user?.isGuest === true,
        loading,
        profileRefreshing,
        login,
        loginWithOAuth,
        ensureUserProfile,
        logout,
        register,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 3. Create the custom hook to consume the context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
